import { Outlet, useLocation, Link } from "react-router-dom"
import { Toaster } from "sonner"
import { LogOut, Lock, Unlock } from "lucide-react"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { ActivityInbox } from "./activity-inbox"
import { AppSidebar } from "./app-sidebar"
import { CommandPalette } from "./command-palette"
import { EnvironmentSwitch } from "./environment-switch"
import { useAuth } from "@/lib/auth"
import { useReadOnly } from "@/lib/read-only"
import { CURRENT_ENVIRONMENT } from "@/lib/deployment"
import { cn } from "@/lib/utils"

const routeNames: Record<string, string> = {
  "/": "Dashboard",
  "/editor": "Workout Editor",
  "/users": "Users",
  "/seasons": "Seasons",
  "/chat": "Chat Edits",
  "/principles": "Principles",
  "/prompts": "Prompts",
  "/agent-sandbox": "Agent Sandbox",
  "/media": "Media Library",
  "/ops": "Ops",
  "/audit": "Flow Audit",
  "/booking-audit": "Booking Audit",
  "/waitlist": "Users & Access",
  "/referrals": "Referrals",
  "/studio-health": "Studio Health",
  "/studio-info": "Studio Information",
  "/settings": "Settings",
}

function BreadcrumbNav() {
  const location = useLocation()
  const segments = location.pathname.split("/").filter(Boolean)

  if (segments.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>Dashboard</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  const crumbs: { label: string; path: string }[] = []
  let currentPath = ""

  for (const segment of segments) {
    currentPath += `/${segment}`
    const label = routeNames[currentPath] || decodeURIComponent(segment)
    crumbs.push({ label, path: currentPath })
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1
          return (
            <span key={crumb.path} className="contents">
              <BreadcrumbItem>
                {!isLast ? (
                  <BreadcrumbLink render={<Link to={crumb.path} />}>
                    {crumb.label}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </span>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export function DashboardLayout() {
  const { user, signOut } = useAuth()
  const { readOnly, toggleReadOnly } = useReadOnly()

  return (
    <TooltipProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header
            className={cn(
              "flex h-16 shrink-0 items-center gap-2 border-b px-4",
              CURRENT_ENVIRONMENT === "staging" && "border-b-2 border-amber-500 bg-amber-50 dark:bg-amber-950/40",
            )}
          >
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <BreadcrumbNav />
            <div className="ml-auto flex items-center gap-3">
              <EnvironmentSwitch />
              <ActivityInbox />
              <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                <span className="text-xs">&#8984;</span>K
              </kbd>
              <button
                onClick={toggleReadOnly}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  readOnly
                    ? "bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                title={readOnly ? "Disable read-only mode" : "Enable read-only mode"}
              >
                {readOnly ? <Lock className="size-3.5" /> : <Unlock className="size-3.5" />}
                <span className="hidden sm:inline">
                  {readOnly ? "READ ONLY" : "Read/Write"}
                </span>
              </button>
              {user && (
                <>
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {user.email}
                  </span>
                  <button
                    onClick={signOut}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    title="Sign out"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </header>
          <main className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
        <CommandPalette />
        <Toaster position="bottom-right" />
      </SidebarProvider>
    </TooltipProvider>
  )
}
